import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChartLine, Target, Brain, Gear, Users, TrendUp, ArrowRight, CheckCircle, Lightning, Database, Scales } from "phosphor-react";
import { updateSeoMetadata, getArticleAlternates } from "../lib/seo";

// Logo URLs (same as Landing.js)
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const WhatIsGeni = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'What is GENI? Automated Trading Journal Explained',
      description: 'Learn what GENI is, how it works, and why traders use it to track and improve performance with real data.',
      path: '/what-is-geni',
      lang: 'en',
      alternates: getArticleAlternates('/what-is-geni'),
    });

    const existingScript = document.querySelector('script[data-schema="faq"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'faq');
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is GENI?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "GENI is an automated trading journal and analysis platform designed to help traders track, analyze, and improve their performance using real data."
          }
        },
        {
          "@type": "Question",
          "name": "Is GENI a trading journal?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, GENI is a trading journal that automates data extraction, calculates performance metrics like win rate and profit factor, and helps traders identify patterns."
          }
        },
        {
          "@type": "Question",
          "name": "Is GENI good for crypto traders?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, GENI is especially optimized for crypto traders with features designed for cryptocurrency markets."
          }
        },
        {
          "@type": "Question",
          "name": "How does GENI work?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "GENI works by allowing traders to upload chart screenshots or manually enter trade data. It extracts key information and calculates performance metrics automatically."
          }
        }
      ]
    });
    document.head.appendChild(script);
    
    return () => {
      document.title = 'GENI';
      const schemaScript = document.querySelector('script[data-schema="faq"]');
      if (schemaScript) schemaScript.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation - EXACT same as Landing.js */}
      <nav className="w-full px-6 py-5 flex items-center justify-between max-w-[1200px] mx-auto">
        {/* Brand - Horizontal Logo */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img 
            src={LOGO_HORIZONTAL} 
            alt="GENI"
            className="h-10 md:h-11 lg:h-12 w-auto object-contain"
          />
        </Link>
        <div className="flex items-center gap-4">
          <Link 
            to="/login" 
            className="text-white/60 hover:text-white text-sm font-medium transition-colors"
          >
            Login
          </Link>
          <Link 
            to="/signup" 
            className="px-4 py-2 bg-white text-black text-sm font-semibold rounded-sm hover:bg-white/90 transition-colors"
          >
            Start tracking
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <nav className="text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#00FF85]">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-400">What is GENI</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">What is GENI?</h1>
        <p className="text-xl text-gray-400 mb-12">Complete Explanation for Traders</p>

        <div className="prose prose-invert max-w-none">
          <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6 mb-12">
            <p className="text-lg text-gray-300 leading-relaxed">
              <strong className="text-white">GENI</strong> is an automated trading journal and analysis platform designed to help traders track, analyze, and improve their performance using real data. Instead of guessing what works and what doesn't, GENI gives you concrete numbers and insights based on your actual trades.
            </p>
          </div>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Gear size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">What GENI Does</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">At its core, GENI solves a fundamental problem in trading: the lack of structured, actionable data about your own performance.</p>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle size={20} className="text-[#00FF85]" />
                  <h3 className="font-semibold text-white">Tracks Trades Automatically</h3>
                </div>
                <p className="text-gray-400 text-sm">Upload chart screenshots and GENI extracts the relevant data without manual input.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle size={20} className="text-[#00FF85]" />
                  <h3 className="font-semibold text-white">Extracts Data from Charts</h3>
                </div>
                <p className="text-gray-400 text-sm">AI-powered analysis reads your chart images and pulls trade information automatically.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle size={20} className="text-[#00FF85]" />
                  <h3 className="font-semibold text-white">Organizes Trading History</h3>
                </div>
                <p className="text-gray-400 text-sm">All your trades in one place, organized by date, asset, setup, and outcome.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <CheckCircle size={20} className="text-[#00FF85]" />
                  <h3 className="font-semibold text-white">Analyzes Performance</h3>
                </div>
                <p className="text-gray-400 text-sm">Automatic calculation of win rate, profit factor, risk/reward, and other key metrics.</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Brain size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Why Traders Need GENI</h2>
            </div>
            <p className="text-gray-300 leading-relaxed mb-6">The vast majority of traders operate without proper data about their own performance.</p>
            <div className="space-y-4 mb-6">
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-amber-400 mb-2">Most traders don't track data properly</h3>
                <p className="text-gray-400 text-sm">Manual journaling takes time. Most traders abandon the process after a few weeks.</p>
              </div>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-amber-400 mb-2">Decisions become emotional</h3>
                <p className="text-gray-400 text-sm">Without data, traders rely on memory and feeling. Memory is unreliable.</p>
              </div>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-amber-400 mb-2">No structured feedback loop</h3>
                <p className="text-gray-400 text-sm">Without a system that shows what's working, traders are guessing.</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Lightning size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">Key Features</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Automated Trade Tracking</h3>
                <p className="text-gray-400 text-sm">Paste chart screenshots and let GENI do the work.</p>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Performance Analytics</h3>
                <p className="text-gray-400 text-sm">Win rate, profit factor, average RR, drawdown calculated automatically.</p>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Setup Comparison</h3>
                <p className="text-gray-400 text-sm">Compare different setups side by side.</p>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <h3 className="font-semibold text-blue-400 mb-2">Behavioral Insights</h3>
                <p className="text-gray-400 text-sm">Identify patterns in your trading behavior.</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Scales size={24} className="text-purple-400" />
              </div>
              <h2 className="text-2xl font-bold">GENI vs Traditional Trading Journals</h2>
            </div>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 text-gray-400"></th>
                    <th className="text-left py-3 text-gray-400">Traditional</th>
                    <th className="text-left py-3 text-[#00FF85]">GENI</th>
                  </tr>
                </thead>
                <tbody className="text-gray-300">
                  <tr className="border-b border-gray-800">
                    <td className="py-3 font-medium">Data Entry</td>
                    <td className="py-3 text-gray-400">Manual</td>
                    <td className="py-3">Automated</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 font-medium">Analysis</td>
                    <td className="py-3 text-gray-400">Manual calculations</td>
                    <td className="py-3">Instant</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-3 font-medium">Consistency</td>
                    <td className="py-3 text-gray-400">Often abandoned</td>
                    <td className="py-3">Easy to maintain</td>
                  </tr>
                  <tr>
                    <td className="py-3 font-medium">Time Required</td>
                    <td className="py-3 text-gray-400">Hours/week</td>
                    <td className="py-3">Minutes/day</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Users size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Who GENI Is For</h2>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-[#00FF85]/10 flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">&#8383;</span>
                </div>
                <h3 className="font-semibold text-white mb-2">Crypto Traders</h3>
                <p className="text-gray-400 text-sm">Built with crypto markets in mind.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <ChartLine size={24} className="text-[#00FF85] mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Day Traders</h3>
                <p className="text-gray-400 text-sm">Efficient tracking for high-frequency traders.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <Target size={24} className="text-[#00FF85] mx-auto mb-3" />
                <h3 className="font-semibold text-white mb-2">Consistency Seekers</h3>
                <p className="text-gray-400 text-sm">Build sustainable, consistent performance.</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <TrendUp size={24} className="text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold">How GENI Helps Improve Trading</h2>
            </div>
            <div className="space-y-4 mb-6">
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} className="text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Identify What Works</h3>
                    <p className="text-gray-400 text-sm">See which setups, assets, and timeframes give you the best results.</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} className="text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Eliminate Bad Habits</h3>
                    <p className="text-gray-400 text-sm">Data reveals patterns you don't notice yourself.</p>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <CheckCircle size={24} className="text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-white mb-2">Improve Consistency</h3>
                    <p className="text-gray-400 text-sm">Build and follow rules with confidence.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Database size={24} className="text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold">Is GENI Only for Crypto?</h2>
            </div>
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 mb-6">
              <p className="text-gray-300 leading-relaxed">
                <strong className="text-white">No</strong> — GENI can be used for any market. However, it is especially optimized for cryptocurrency traders with features designed for the unique aspects of crypto trading.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF85]/10 flex items-center justify-center">
                <Target size={24} className="text-[#00FF85]" />
              </div>
              <h2 className="text-2xl font-bold">Final Summary</h2>
            </div>
            <div className="bg-[#00FF85]/5 border border-[#00FF85]/20 rounded-xl p-6 mb-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                <strong className="text-white">GENI is not just a journal</strong> — it is a system for improving trading performance through structured data and analysis. It automates the tedious parts of trade tracking and helps traders identify patterns they wouldn't see otherwise.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Start Understanding Your Trading</h2>
              <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                Try GENI and start understanding your trading with real data.
              </p>
              <Link to="/signup" className="inline-flex items-center gap-2 bg-[#00FF85] text-black px-8 py-3 rounded-lg font-semibold hover:bg-[#00DD70] transition-colors">
                Try GENI Free <ArrowRight size={20} />
              </Link>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">What is GENI?</h3>
                <p className="text-gray-400 text-sm">GENI is an automated trading journal and analysis platform designed to help traders track, analyze, and improve their performance using real data.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Is GENI a trading journal?</h3>
                <p className="text-gray-400 text-sm">Yes, GENI is a trading journal that automates data extraction, calculates performance metrics, and helps traders identify patterns.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">Is GENI good for crypto traders?</h3>
                <p className="text-gray-400 text-sm">Yes, GENI is especially optimized for crypto traders with features designed for cryptocurrency markets.</p>
              </div>
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">How does GENI work?</h3>
                <p className="text-gray-400 text-sm">GENI works by allowing traders to upload chart screenshots or manually enter trade data. It extracts key information and calculates performance metrics automatically.</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-bold mb-6">Learn More</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/automated-trading-journal" className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group">
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Automated Trading Journal</h3>
                <p className="text-gray-500 text-sm">Why automated journals outperform manual tracking.</p>
              </Link>
              <Link to="/crypto-trading-journal" className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group">
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Crypto Trading Journal</h3>
                <p className="text-gray-500 text-sm">How to track and analyze your crypto trades.</p>
              </Link>
              <Link to="/how-to-analyze-your-trades" className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group">
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">How to Analyze Your Trades</h3>
                <p className="text-gray-500 text-sm">A practical guide to trade analysis.</p>
              </Link>
              <Link to="/profit-factor-trading" className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 hover:border-[#00FF85]/50 transition-colors group">
                <h3 className="font-semibold text-white mb-2 group-hover:text-[#00FF85] transition-colors">Profit Factor Explained</h3>
                <p className="text-gray-500 text-sm">Understanding the key metric of trading quality.</p>
              </Link>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-gray-800/50 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img 
                src={LOGO_HORIZONTAL} 
                alt="GENI"
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-white transition-colors">About</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            </div>
            <p className="text-sm text-gray-600">© 2025 GENI. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WhatIsGeni;
